/**
 * changelog.js — historial de versiones para el modal "Novedades".
 *
 * Cómo usarlo en cada release:
 *   1. Agregá un nuevo objeto AL PRINCIPIO del array (el más reciente primero).
 *   2. Usá un número de versión más alto que el anterior (comparación simple
 *      de string — mantené el formato "X.Y.Z" para que ordene bien).
 *   3. Cada `items[]` es una línea de la lista; `type` define el color/ícono:
 *      "feature" (nuevo, naranja), "fix" (corrección, verde), "mejora" (azul).
 *   4. Cada item tiene un `aud` (audiencia): "admin" (default, no hace falta
 *      escribirlo), "residente" o "ambos". Un residente en el Portal solo ve
 *      los items marcados "residente" o "ambos" — nunca los de contabilidad.
 *
 * El modal se muestra automáticamente una vez por usuario/versión (guarda la
 * última versión vista en localStorage) — no hace falta tocar nada más.
 */
const CHANGELOG = [
  {
    version: "1.5.1",
    date: "2026-08-02",
    title: "NIIF 18 en Resultados, clasificación de cuentas y demo comercial",
    items: [
      { type: "feature", text: "El Estado de Resultados ahora sigue la estructura NIIF 18: separa Operación, Inversión, Financiamiento e Impuesto sobre la Renta, con los subtotales de Utilidad Operativa, Utilidad antes de Financiamiento e Impuestos y Utilidad del Período. Aplica también dentro del Estado Financiero completo." },
      { type: "feature", text: "Nuevo campo de clasificación en el Catálogo de Cuentas: agrupa y rotula las partidas del Flujo de Efectivo y del Estado de Cambios en el Patrimonio con el texto que le asignes a cada cuenta, en vez del nombre de catálogo." },
      { type: "feature", text: "Rediseño del Estado de Cuenta (Cuentas por Cobrar y por Pagar): mismo formato que el resto de los estados financieros, con selector de color de marca igual al del Reporte Ejecutivo." },
      { type: "feature", text: "Nueva pestaña \"Demo comercial\" en el Panel de Administración para crear un condominio de prueba con datos realistas y compartirlo con prospectos." },
      { type: "fix", text: "El mensaje de saldo del Estado de Cuenta ahora indica \"Saldo Adeudado\", \"Saldo a Favor\" o \"Sin Saldo Pendiente\" según corresponda, en vez de un genérico \"Saldo al\"." },
      { type: "fix", text: "Corregido un desbordamiento del encabezado del Estado de Cuenta cuando el nombre del cliente era muy largo, y agregada paginación real cuando hay muchos movimientos en el período." },
      { type: "fix", text: "El modal de enviar el Estado de Cuenta por correo (Cuentas por Cobrar) ya no se cierra antes de mostrar si el envío fue exitoso." },
      { type: "fix", text: "Corregidos los colores de la página de Presupuesto, que no se adaptaban al tema claro/oscuro del resto del sistema." },
      { type: "fix", text: "El aviso de \"Novedades\" ahora aparece una sola vez por versión nueva, en vez de en cada inicio de sesión." },
      { type: "mejora", text: "Las fechas del Estado de Cuenta ahora vienen precargadas con el año en curso, en vez de salir vacías." },
    ],
  },
  {
    version: "1.5.0",
    date: "2026-07-24",
    title: "Informes, moneda ₡/$ y rediseño visual",
    items: [
      { type: "feature", text: "Nuevo apartado de Informes: gráficas y filtros avanzados (cliente/proveedor, rubro, fechas, estado, moneda) para CxC y CxP." },
      { type: "feature", text: "Toggle ₡ / $ en el Dashboard, en los listados de CxC y CxP, y en Informes." },
      { type: "feature", text: "Rediseño de Cuentas Bancarias: tarjetas visuales y todas las acciones (crear, editar, eliminar, saldo inicial) ahora en modales." },
      { type: "feature", text: "Rediseño del Panel de Administración para que combine con el resto del sistema (tema claro/oscuro)." },
      { type: "feature", text: "Aviso automático cuando hay una versión nueva del sistema disponible, sin tener que refrescar a ciegas.", aud: "ambos" },
      { type: "fix", text: "Corregidos los totales cruzados de colones/dólares en el Dashboard y en los listados de CxC/CxP cuando había facturas mezcladas en ambas monedas." },
      { type: "fix", text: "Corregida la lógica de abono multimoneda en Recibos CxP: ahora se comporta igual que en CxC." },
      { type: "fix", text: "Los modales de todo el sistema ya no se cierran solos al arrastrar una selección de texto desde adentro hacia afuera del cuadro.", aud: "ambos" },
      { type: "fix", text: "Importar clientes desde Excel ahora refresca la lista automáticamente al terminar, sin tener que recargar la página." },
      { type: "fix", text: "Eliminar un cliente ahora borra también su registro de Filial (evita que quede huérfano y siga apareciendo en Facturas Masivas u otras pantallas)." },
      { type: "fix", text: "El buscador de Personería Jurídica (TSE) ya no invierte el nombre de sociedades, y lo escribe siempre en MAYÚSCULAS." },
      { type: "fix", text: "La plantilla de importación de Proveedores ya no marca todos los campos como obligatorios - solo Tipo de cédula, Cédula y Nombre lo son." },
      { type: "fix", text: "Corregido un residuo de centavos por redondeo de moneda que a veces generaba una línea extra de \"Saldo a favor\" además del Diferencial cambiario en el asiento de un recibo." },
      { type: "mejora", text: "Búsqueda con autocompletado para proveedores, clientes y rubros en varios formularios de facturas y recibos." },
      { type: "mejora", text: "Modal de eliminar proveedor rediseñado para coincidir con el estilo del modal de eliminar clientes." },
      { type: "mejora", text: "Ajustes visuales en Editar Factura: el campo Monto y el selector de tarifa de IVA ahora tienen el tamaño correcto." },
      { type: "mejora", text: "La plantilla de Saldos Iniciales y la pantalla de Facturas Masivas usan el vocabulario de \"Cliente\" en vez de \"Filial\" cuando el sistema está en modo Cliente." },
    ],
  },
];

export default CHANGELOG;

/** Versión más reciente del changelog (independiente del rol). */
export function ultimaVersion() {
  return CHANGELOG[0]?.version ?? "0.0.0";
}

export const STORAGE_KEY = "changelog_seen_version";

function esRolResidente(rol) {
  return (rol ?? localStorage.getItem("rol")) === "RESIDENTE";
}

/**
 * Devuelve el changelog recortado a lo que le corresponde ver a ese rol:
 * un residente nunca ve items de contabilidad/administración, y las
 * versiones que no tengan ningún item relevante para él se descartan.
 */
export function changelogParaRol(rol) {
  const clave = esRolResidente(rol) ? "residente" : "admin";
  return CHANGELOG
    .map((version) => ({
      ...version,
      items: version.items.filter((it) => (it.aud ?? "admin") === clave || it.aud === "ambos"),
    }))
    .filter((version) => version.items.length > 0);
}

/** true si el usuario todavía no vio la versión más reciente que le aplica. */
export function hayNovedadesSinVer(rol) {
  try {
    if (changelogParaRol(rol).length === 0) return false;
    return localStorage.getItem(STORAGE_KEY) !== ultimaVersion();
  } catch {
    return false;
  }
}

/** Marca la versión más reciente como vista (no se vuelve a auto-abrir). */
export function marcarNovedadesVistas() {
  try { localStorage.setItem(STORAGE_KEY, ultimaVersion()); } catch { /* noop */ }
}
